import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PromptTagListRow } from "./PromptTagListRow";
import { usePromptTags } from "./usePromptTags";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onChanged?: () => void;
};

export const PromptTagsManager = ({ open, onOpenChange, workspaceId, onChanged }: Props) => {
  const { loading, busy, allItems, deleteTags, renameTag, addPhantom, resetPhantoms } =
    usePromptTags(workspaceId, open);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{ tag: string; value: string } | null>(null);
  const [adding, setAdding] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setEditing(null);
    setAdding("");
    resetPhantoms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  const toggle = (key: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTags(confirmDelete);
      setSelected(new Set());
      setConfirmDelete(null);
      onChanged?.();
    } catch {
      // toast handled in hook
    }
  };

  const handleRename = async (oldKey: string, newName: string) => {
    const ok = await renameTag(oldKey, newName);
    if (ok) {
      setEditing(null);
      onChanged?.();
    }
  };

  const handleAdd = () => {
    if (addPhantom(adding)) setAdding("");
    else if (!adding.trim()) return;
    else setAdding("");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage prompt tags</DialogTitle>
            <DialogDescription>
              Rename or delete tags across all your prompts. Changes apply to every prompt that
              uses the tag.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <Input
              placeholder="Add a new tag…"
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              className="h-8"
            />
            <Button size="sm" variant="outline" onClick={handleAdd} disabled={!adding.trim()}>
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          </div>

          <div className="max-h-80 overflow-auto rounded-md border border-border/60">
            {loading ? (
              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : allItems.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">
                No tags yet. Add tags by editing a prompt or use the field above.
              </p>
            ) : (
              <ul className="divide-y divide-border/60">
                {allItems.map((t) => (
                  <PromptTagListRow
                    key={t.key}
                    item={t}
                    isSelected={selected.has(t.key)}
                    isEditing={editing?.tag === t.key}
                    editingValue={editing?.tag === t.key ? editing.value : ""}
                    busy={busy}
                    onToggle={() => toggle(t.key)}
                    onEditStart={() => setEditing({ tag: t.key, value: t.name })}
                    onEditChange={(v) =>
                      setEditing((cur) => (cur ? { ...cur, value: v } : cur))
                    }
                    onEditCancel={() => setEditing(null)}
                    onEditSave={() => editing && void handleRename(t.key, editing.value)}
                    onDelete={() => setConfirmDelete([t.key])}
                  />
                ))}
              </ul>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="destructive"
              size="sm"
              disabled={selected.size === 0 || busy}
              onClick={() => setConfirmDelete(Array.from(selected))}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete selected ({selected.size})
            </Button>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {confirmDelete?.length ?? 0} tag{(confirmDelete?.length ?? 0) === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the tag from every prompt that uses it. The prompts themselves are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PromptTagsManager;
